import { useState } from "react";

import { Plus, Trash2, Pencil, Sparkles } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/components/ui/sonner";
import { Textarea } from "@/components/ui/textarea";
import { useEdgeFunction } from "@/hooks/useEdgeFunction";
import { useCreateBoard, CreateBoardPersona } from "@/hooks/useCreateBoard";
import { SuggestedPersona } from "@/types/state";

interface PersonaSuggestionsProps {
  personas: SuggestedPersona[];
  problem: string;
  context: string;
  onUpdatePersonas: (personas: SuggestedPersona[]) => void;
  onSuccess?: (boardId: string) => void;
}

const PersonaSuggestions = ({ 
  personas, 
  problem, 
  context, 
  onUpdatePersonas,
  onSuccess
}: PersonaSuggestionsProps) => {
  const { createBoard, isCreating } = useCreateBoard();
  const { execute: invoke } = useEdgeFunction({ auth: true });
  const [editingPersona, setEditingPersona] = useState<SuggestedPersona | null>(null);
  const [generatingQueue, setGeneratingQueue] = useState<string[]>([]);
  const [currentlyGenerating, setCurrentlyGenerating] = useState<string | null>(null);
  const [boardName, setBoardName] = useState("");
  const [showFinalDialog, setShowFinalDialog] = useState(false);

  const handleAddCustom = () => {
    const newPersona: SuggestedPersona = {
      id: `custom-${Date.now()}`,
      role_name: "",
      justification: "Custom expert added by user"
    };
    onUpdatePersonas([...personas, newPersona]);
  };

  const handleDelete = (id: string) => {
    onUpdatePersonas(personas.filter(p => p.id !== id));
  };

  const handleUpdateRoleName = (id: string, role_name: string) => {
    onUpdatePersonas(personas.map(p => p.id === id ? { ...p, role_name } : p));
  };

  const processQueue = async (queue: string[]) => {
    if (queue.length === 0 || currentlyGenerating) return;

    const personaId = queue[0];
    const persona = personas.find(p => p.id === personaId);
    
    if (!persona) {
      setGeneratingQueue(prev => prev.slice(1));
      return;
    }

    setCurrentlyGenerating(personaId);

    try {
      const allRoles = personas.map(p => p.role_name).filter(r => r.trim());
      
      const { data, error } = await invoke("generate-persona-details", {
        body: {
          roleName: persona.role_name,
          problem,
          context,
          allRoles
        }
      });

      if (error) {
        toast.error(`Failed to generate details for ${persona.role_name}`);
        return;
      }

      const updatedPersona = {
        ...persona,
        details: data
      };

      const updatedPersonas = personas.map(p => p.id === persona.id ? updatedPersona : p);
      onUpdatePersonas(updatedPersonas);
      setEditingPersona(updatedPersona);
      toast.success(`Details generated for ${persona.role_name}!`);
    } catch (error) {
      console.error("Error generating details:", error);
      toast.error(`Failed to generate details for ${persona.role_name}`);
    } finally {
      setCurrentlyGenerating(null);
      setGeneratingQueue(prev => {
        const newQueue = prev.slice(1);
        if (newQueue.length > 0) {
          setTimeout(() => processQueue(newQueue), 100);
        }
        return newQueue;
      });
    }
  };

  const handleGenerateDetails = async (persona: SuggestedPersona) => {
    if (!persona.role_name.trim()) {
      toast.error("Please enter a role name first");
      return;
    }

    // If clicking the currently generating persona, cancel it
    if (currentlyGenerating === persona.id) {
      setCurrentlyGenerating(null);
      setGeneratingQueue(prev => prev.filter(id => id !== persona.id));
      toast.info("Generation cancelled");
      return;
    }

    // If already in queue, don't add again
    if (generatingQueue.includes(persona.id)) {
      return;
    }

    // Add to queue
    const newQueue = [...generatingQueue, persona.id];
    setGeneratingQueue(newQueue);

    // Start processing if not already running
    if (!currentlyGenerating) {
      processQueue(newQueue);
    }
  };

  const handleGenerateAll = () => {
    const personasWithoutDetails = personas.filter(p => !p.details && p.role_name.trim());
    
    if (personasWithoutDetails.length === 0) {
      toast.error("All personas already have details or need role names");
      return;
    }

    const personasNeedingNames = personas.filter(p => !p.details && !p.role_name.trim());
    if (personasNeedingNames.length > 0) {
      toast.error("Please add role names to all personas first");
      return;
    }

    // Add all personas without details to the queue
    const newIds = personasWithoutDetails.map(p => p.id).filter(id => !generatingQueue.includes(id));
    const newQueue = [...generatingQueue, ...newIds];
    setGeneratingQueue(newQueue);

    toast.success(`Queued ${newIds.length} persona(s) for generation`);

    // Start processing if not already running
    if (!currentlyGenerating) {
      processQueue(newQueue);
    }
  };

  const handleSavePersonaDetails = (persona: SuggestedPersona) => {
    onUpdatePersonas(personas.map(p => p.id === persona.id ? persona : p));
    setEditingPersona(null);
    toast.success("Persona details saved");
  };

  const handleFinalizeBoard = () => {
    const incompletePersonas = personas.filter(p => !p.details);
    if (incompletePersonas.length > 0) {
      toast.error("Please generate details for all personas before creating the board");
      return;
    }

    if (personas.length === 0) {
      toast.error("Please add at least one persona");
      return;
    }

    setBoardName("My Custom Board");
    setShowFinalDialog(true);
  };

  const handleCreateBoard = async () => {
    if (!boardName.trim()) {
      toast.error("Please enter a board name");
      return;
    }

    const personasToCreate = personas.map(persona => ({
      role_name: persona.role_name,
      mission: persona.details!.mission,
      principles: persona.details!.principles,
      competencies: persona.details!.competencies,
      key_relationships: persona.details!.key_relationships,
      typical_questions: persona.details!.typical_questions
    }));

    await createBoard({
      boardName,
      description: `Custom board for: ${problem}`,
      personas: personasToCreate as CreateBoardPersona[],
      boardType: "custom",
      onSuccess
    });
  };

  return (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <h1 className="text-3xl font-semibold">Your Suggested Experts</h1>
        <p className="text-muted-foreground">
          Review, edit, and generate details for each expert
        </p>
        <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-muted/50 rounded-lg">
          <Sparkles className="h-4 w-4 text-primary" />
          <span className="font-medium">
            {personas.filter(p => p.details).length} of {personas.length} personas generated
          </span>
        </div>
      </div>

      <div className="space-y-4">
        {personas.map((persona) => (
          <Card key={persona.id} className={persona.details ? "bg-green-50/50 dark:bg-green-950/20 border-green-200/50 dark:border-green-900/30 transition-colors" : ""}>
            <CardHeader>
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 space-y-2">
                  <Input
                    value={persona.role_name}
                    onChange={(e) => handleUpdateRoleName(persona.id, e.target.value)}
                    placeholder="Enter role name"
                    className="font-semibold"
                  />
                  <CardDescription>{persona.justification}</CardDescription>
                  {persona.details && (
                    <p className="text-sm text-green-600">✓ Details generated</p>
                  )}
                  {generatingQueue.includes(persona.id) && currentlyGenerating !== persona.id && (
                    <p className="text-sm text-muted-foreground">⏳ Queued...</p>
                  )}
                </div>
                <div className="flex gap-2">
                  {persona.details && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setEditingPersona(persona)}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleDelete(persona.id)}
                  >
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            {!persona.details && (
              <CardContent>
                <Button
                  size="sm"
                  onClick={() => handleGenerateDetails(persona)}
                  variant={currentlyGenerating === persona.id ? "destructive" : "default"}
                >
                  <Sparkles className="h-4 w-4 mr-2" />
                  {currentlyGenerating === persona.id 
                    ? "Cancel Generation" 
                    : generatingQueue.includes(persona.id)
                    ? "Queued..."
                    : "Generate Details"}
                </Button>
              </CardContent>
            )}
          </Card>
        ))}
      </div>

      <div className="flex gap-2">
        <Button variant="outline" onClick={handleAddCustom}>
          <Plus className="h-4 w-4 mr-2" />
          Add Custom Expert
        </Button>
        <Button 
          variant="secondary" 
          onClick={handleGenerateAll}
          disabled={personas.filter(p => !p.details && p.role_name.trim()).length === 0 || currentlyGenerating !== null}
        >
          <Sparkles className="h-4 w-4 mr-2" />
          Generate All
        </Button>
        <Button onClick={handleFinalizeBoard} className="flex-1">
          Create Board
        </Button>
      </div>

      {editingPersona && (
        <Dialog open={!!editingPersona} onOpenChange={() => setEditingPersona(null)}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Edit {editingPersona.role_name}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Mission</Label>
                <Textarea
                  value={editingPersona.details?.mission || ""}
                  onChange={(e) => setEditingPersona({
                    ...editingPersona,
                    details: { ...editingPersona.details!, mission: e.target.value }
                  })}
                  rows={3}
                />
              </div>
              <div>
                <Label>Guiding Principles & Philosophy</Label>
                <Textarea
                  value={editingPersona.details?.principles || ""}
                  onChange={(e) => setEditingPersona({
                    ...editingPersona,
                    details: { ...editingPersona.details!, principles: e.target.value }
                  })}
                  rows={3}
                />
              </div>
              <div>
                <Label>Core Competencies</Label>
                <Textarea
                  value={editingPersona.details?.competencies || ""}
                  onChange={(e) => setEditingPersona({
                    ...editingPersona,
                    details: { ...editingPersona.details!, competencies: e.target.value }
                  })}
                  rows={3}
                />
              </div>
              <div>
                <Label>Key Relationships</Label>
                <Textarea
                  value={editingPersona.details?.key_relationships || ""}
                  onChange={(e) => setEditingPersona({
                    ...editingPersona,
                    details: { ...editingPersona.details!, key_relationships: e.target.value }
                  })}
                  rows={2}
                />
              </div>
              <div>
                <Label>Typical Questions</Label>
                <Textarea
                  value={editingPersona.details?.typical_questions || ""}
                  onChange={(e) => setEditingPersona({
                    ...editingPersona,
                    details: { ...editingPersona.details!, typical_questions: e.target.value }
                  })}
                  rows={2}
                />
              </div>
              <Button onClick={() => handleSavePersonaDetails(editingPersona)} className="w-full">
                Save Changes
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      <Dialog open={showFinalDialog} onOpenChange={setShowFinalDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Name Your Board</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="finalBoardName">Board Name</Label>
              <Input
                id="finalBoardName"
                value={boardName}
                onChange={(e) => setBoardName(e.target.value)}
                placeholder="e.g., My Custom Advisory Board"
              />
            </div>
            <Button 
              onClick={handleCreateBoard} 
              disabled={isCreating}
              className="w-full"
            >
              {isCreating ? "Creating..." : "Create Board & Start Session"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PersonaSuggestions;
